import { createContext, use, useReducer, useRef } from "react";
import type { PaymentMethod, TshirtSize, TravelMode } from "@satyrsmc/shared/client";
import { includesTshirt, travelModesFor } from "@satyrsmc/shared/lib/day-pass-policy";

export interface CampoutDetailsState {
  tshirtSize: TshirtSize | null;
  travelingBy: TravelMode | null;
  club: string;
}

/**
 * Whether the registrant is coming for the whole event or buying a day pass.
 *
 * Modelled as one field rather than a boolean plus a date so the two can never
 * disagree — "single day, none picked yet" is a real state the wizard must be
 * able to hold, and it has to block Next without being mistaken for a full-event
 * registration when the RSVP is submitted. The initial `unset` state forces an
 * explicit choice: defaulting to `full` is how a re-registration silently
 * dropped a registrant's single day.
 */
export type AttendanceChoice =
  { kind: "unset" } | { kind: "full" } | { kind: "single"; date: string | null };

export interface WizardState {
  step: number;
  bikeId: string | null;
  isRider: boolean | null;
  dietaryRestrictions: string | null;
  attendance: AttendanceChoice;
  campoutDetails: CampoutDetailsState | null;
  waiverSigned: boolean;
  paymentMethod: PaymentMethod | null;
  stripeClientSecret: string | null;
  externalPaymentId: string | null;
}

type WizardAction =
  | { type: "SET_STEP"; step: number }
  | { type: "SET_BIKE_ID"; bikeId: string | null }
  | { type: "SET_IS_RIDER"; isRider: boolean | null }
  | { type: "SET_DIETARY_RESTRICTIONS"; value: string | null }
  | { type: "SET_ATTENDANCE"; attendance: AttendanceChoice }
  | { type: "SET_CAMPOUT_DETAILS"; details: CampoutDetailsState }
  | { type: "SET_WAIVER_SIGNED"; signed: boolean }
  | { type: "SET_PAYMENT_METHOD"; method: PaymentMethod | null }
  | { type: "SET_STRIPE_CLIENT_SECRET"; secret: string | null }
  | { type: "SET_EXTERNAL_PAYMENT_ID"; id: string };

/** Strip campout answers that this kind of registration does not include. */
function applyDayPassRules(details: CampoutDetailsState, isDayPass: boolean): CampoutDetailsState {
  const allowedModes = travelModesFor(isDayPass);
  return {
    ...details,
    tshirtSize: includesTshirt(isDayPass) ? details.tshirtSize : null,
    travelingBy:
      details.travelingBy && !allowedModes.includes(details.travelingBy)
        ? null
        : details.travelingBy,
  };
}

function reducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case "SET_STEP":
      return { ...state, step: action.step };
    case "SET_BIKE_ID":
      return { ...state, bikeId: action.bikeId };
    case "SET_IS_RIDER":
      return { ...state, isRider: action.isRider };
    case "SET_DIETARY_RESTRICTIONS":
      return { ...state, dietaryRestrictions: action.value };
    case "SET_ATTENDANCE": {
      const { attendance } = action;
      // The attendance choice sets the price, so any payment intent already quoted
      // is now for the wrong amount. Dropping the secret forces a fresh quote —
      // without this, going back and switching would confirm a card payment for
      // the old choice against the new registration.
      const repriced = { ...state, attendance, stripeClientSecret: null };
      if (!state.campoutDetails) return repriced;
      // Switching between full event and day pass changes which answers are even
      // asked for. Drop the ones the form is about to stop showing, so a size or
      // an RV picked beforehand can't ride along invisibly into the submission.
      return {
        ...repriced,
        campoutDetails: applyDayPassRules(state.campoutDetails, attendance.kind === "single"),
      };
    }
    case "SET_CAMPOUT_DETAILS":
      return { ...state, campoutDetails: action.details };
    case "SET_WAIVER_SIGNED":
      return { ...state, waiverSigned: action.signed };
    case "SET_PAYMENT_METHOD":
      return { ...state, paymentMethod: action.method, stripeClientSecret: null };
    case "SET_STRIPE_CLIENT_SECRET":
      return { ...state, stripeClientSecret: action.secret };
    case "SET_EXTERNAL_PAYMENT_ID":
      return { ...state, externalPaymentId: action.id };
    default:
      return state;
  }
}

const initialState: WizardState = {
  step: 0,
  bikeId: null,
  isRider: null,
  dietaryRestrictions: null,
  attendance: { kind: "unset" },
  campoutDetails: null,
  waiverSigned: false,
  paymentMethod: null,
  stripeClientSecret: null,
  externalPaymentId: null,
};

interface WizardContextValue {
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
  stripeConfirmRef: React.MutableRefObject<(() => Promise<string>) | null>;
}

const WizardContext = createContext<WizardContextValue | null>(null);

export function WizardProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const stripeConfirmRef = useRef<(() => Promise<string>) | null>(null);
  return <WizardContext value={{ state, dispatch, stripeConfirmRef }}>{children}</WizardContext>;
}

export function useWizard() {
  const ctx = use(WizardContext);
  if (!ctx) throw new Error("useWizard must be used inside WizardProvider");
  return ctx;
}
